import BulletList from '@tiptap/extension-bullet-list'
import Heading from '@tiptap/extension-heading'
import Mention from '@tiptap/extension-mention'
import {
  EditorProvider,
  type JSONContent,
  mergeAttributes,
  ReactRenderer,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import tippy from 'tippy.js'

import { cn } from '../../../lib/utils'

import { Variable } from './extensions/variable'
import { MentionList } from './ui/mention-list'
import { MenuBar } from './ui/menu-bar'

export { RichTextEditorContext } from './state'

// define your extension array
const extensions = [
  Variable,
  Mention.configure({
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: {
      command: ({ editor, range, props }) => {
        // Insert your custom node instead of mention
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: 'variable',
              attrs: {
                id: props.id,
                label: props.label,
                // @ts-ignore
                type: props.type,
              },
            },
            {
              type: 'text',
              text: ' ',
            },
          ])
          .run()
      },
      items: ({ query }) => {
        return [
          {
            id: 'query',
            label: query,
          },
        ]
      },
      render: () => {
        let reactRenderer: ReactRenderer
        let popup: any

        return {
          onStart: (props) => {
            if (!props.clientRect) {
              return
            }

            reactRenderer = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            })

            // @ts-ignore
            popup = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: reactRenderer.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'right-start',
            })
          },

          onUpdate(props) {
            reactRenderer.updateProps(props)

            if (!props.clientRect) {
              return
            }

            popup[0].setProps({
              getReferenceClientRect: props.clientRect,
            })
          },

          onKeyDown(props) {
            if (props.event.key === 'Escape') {
              popup[0].hide()

              return true
            }

            // @ts-ignore
            return reactRenderer.ref?.onKeyDown(props)
          },

          onExit() {
            popup[0].destroy()
            reactRenderer.destroy()
          },
        }
      },
    },
  }),
  BulletList.configure({
    HTMLAttributes: {
      class: 'list-disc ml-6',
    },
  }),
  StarterKit.configure({
    heading: false,
    bulletList: false,
    orderedList: {
      HTMLAttributes: {
        class: 'list-decimal ml-6',
      },
    },
  }),
  Heading.extend({
    levels: [1, 2],
    renderText({ node }) {
      // markdown
      return `${'#'.repeat(node.attrs.level)} ${node.textContent}`
    },
    renderHTML({ node, HTMLAttributes }) {
      const level = this.options.levels.includes(node.attrs.level)
        ? node.attrs.level
        : this.options.levels[0]
      const classes: { [index: number]: string } = {
        1: 'font-heading-h2',
        2: 'font-heading-h3',
        3: 'font-heading-h4',
      }
      return [
        `h${level}`,
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          class: `${classes[level]}`,
        }),
        0,
      ]
    },
  }).configure({ levels: [1, 2, 3] }),
]

export function findNodesByType(
  doc: JSONContent,
  nodeType: 'variable' | 'tool',
) {
  const results: { node: JSONContent; path: any[] }[] = []

  function traverse(node: JSONContent, path: any[] = []) {
    if (node.type === nodeType) {
      results.push({
        node: node,
        path: path,
      })
    }

    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((child: any, index: number) => {
        traverse(child, [...path, 'content', index])
      })
    }
  }

  traverse(doc)
  return results
}

interface RichTextEditorProps {
  className?: string
  onBlur?: (markdown: string) => void
  onChange: (htmlString: string) => void
  onChangeJSON?: (json: JSONContent) => void
  readOnly?: boolean
  text?: string | undefined
}

const RichTextEditor = ({
  text,
  onChange,
  onChangeJSON,
  onBlur,
  className,
  readOnly = false,
}: RichTextEditorProps) => {
  const editorProps = {
    attributes: {
      class: cn([
        'resize-none prose-sm bg-input w-full h-96 overflow-y-auto rounded-md border py-m outline-hidden font-text-s',
        'placeholder:text-input hover:border-input disabled:cursor-not-allowed disabled:opacity-50 px-l focus-visible:ring-3 focus-visible:ring-primary gap-x-2',
        readOnly && 'text-input',
        className,
      ]),
    },
  }

  return (
    <div className="gap-y-s flex flex-col">
      <EditorProvider
        content={text}
        editorProps={editorProps}
        extensions={extensions}
        onBlur={({ editor }) => {
          if (onBlur) {
            onBlur(editor.getHTML())
          }
          if (onChangeJSON) {
            onChangeJSON(editor.getJSON())
          }
        }}
        onUpdate={({ editor }) => {
          onChange(!editor.isEmpty ? editor.getHTML() : '')
        }}
        slotBefore={<MenuBar />}
      />
    </div>
  )
}

export { RichTextEditor }
